import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, Input, SectionTitle, StatusBadge, Table, Textarea } from "../ui";
import { bursariesApi } from "../../api/hliLists";
import { errorMessageOf } from "../../api/scholarships";
import { claimDisplayStatus } from "../../config/claimStatus";
import { formatDateTime } from "../scholarships/review/model";
import { TOAST } from "./shared";

const CLAIMS_KEY = ["bursaries", "me", "result-claims"];

const COLUMNS = [
  { key: "claimType", label: "Claim" },
  { key: "description", label: "Reason", render: (c) => <span className="line-clamp-2 max-w-xs">{c.description}</span> },
  { key: "status", label: "Status", render: (c) => <StatusBadge status={claimDisplayStatus(c.status)} /> },
  { key: "createdAt", label: "Submitted", accessor: (c) => formatDateTime(c.createdAt), className: "whitespace-nowrap" },
];

// A claim for a review of results, on the student's record, and the claims made so far
export default function ResultClaimTab({ record }) {
  const queryClient = useQueryClient();
  const [claimType, setClaimType] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: claims = [], isLoading } = useQuery({
    queryKey: CLAIMS_KEY,
    queryFn: async () => (await bursariesApi.myResultClaims()).data,
  });

  const send = async (event) => {
    event.preventDefault();
    if (!claimType.trim() || !reason.trim()) {
      toast.warning("Please, provide all necessary information", TOAST);
      return;
    }
    setBusy(true);
    try {
      await bursariesApi.fileResultClaim({ claimType: claimType.trim(), description: reason.trim() });
      setClaimType("");
      setReason("");
      toast.success("Your claim is received, we will give you the response soon.", TOAST);
      queryClient.invalidateQueries({ queryKey: CLAIMS_KEY });
    } catch (error) {
      console.error("Failed to submit the claim", error);
      toast.error(errorMessageOf(error, "Please, try again"), TOAST);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <form onSubmit={send} className="flex max-w-2xl flex-col gap-5">
        <Input label="Registration number" value={record.registrationNumber || ""} readOnly />
        <Input
          label="Please, tell us about your claim"
          required
          value={claimType}
          onChange={(e) => setClaimType(e.target.value)}
          placeholder="Describe your claim…"
          maxLength={255}
        />
        <Textarea
          label="Reason"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Please explain clearly why you are claiming for a result review."
          maxLength={2000}
          info={`${reason.length}/2000 characters`}
        />
        <Button type="submit" loading={busy} icon={<PaperAirplaneIcon className="h-4 w-4" />} className="self-start">
          Submit claim
        </Button>
      </form>
      <section className="flex flex-col gap-3">
        <SectionTitle title="Your claims" />
        <Table columns={COLUMNS} data={claims} isLoading={isLoading} searchable={false} exportable={false} emptyTitle="You have not submitted a claim yet" />
      </section>
    </div>
  );
}
